// react
import {
	addDays,
	addMonths,
	format,
	getDay,
	getDaysInMonth,
	isValid,
	setHours,
	setMinutes,
	setYear,
	startOfMonth,
	subDays,
} from "date-fns";
// dependencies
import moment from "moment";
import React, { BaseSyntheticEvent, useMemo, useState } from "react";

import { faClock } from "@fortawesome/pro-regular-svg-icons";
import { faArrowAltCircleLeft, faArrowAltCircleRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Day from "./CalendarDay";
// config
import Config from "./Config";

// styles
import css from "./DatePicker.module.scss";

interface DatePickerProps {
	isShowing?: boolean;
	value?: Date | string;
	activeMonth?: Date;
	// width must be equal to or greater than 184
	width: number;
	defaultTime: number;
	hasTime: boolean;
	changeActiveMonth: (date: Date) => void;
	setDate: (date: Date) => void;
}

export const CALENDAR_MIN_WIDTH = 184;
export const CALENDAR_MAX_WIDTH = 475;

const Calendar = ({
	isShowing = true,
	value = "",
	activeMonth = startOfMonth(new Date()),
	width,
	defaultTime,
	hasTime,
	changeActiveMonth,
	setDate,
}: DatePickerProps) => {
	const [yearHasFocus, setYearHasFocus] = useState(false);
	const [timeIsShowing, setTimeIsShowing] = useState(false);
	const [yearValue, setYearValue] = useState(format(activeMonth, "yyyy"));
	const dateValue = useMemo(() => {
		if (moment.isMoment(value)) {
			return value.toDate();
		} else if (value instanceof Date) {
			return value;
		} else if (isValid(new Date(value))) {
			return new Date(value);
		} else {
			return null;
		}
	}, [value]);
	const selectedTimeValue =
		dateValue && Config.timeOptions.map((time) => time.value).includes(dateValue.getHours())
			? dateValue?.getHours()
			: defaultTime;

	// set min calendar width so it doesn't get too small
	const calendarWidth = Math.max(width, CALENDAR_MIN_WIDTH);

	const toggleTime = () => {
		setTimeIsShowing(!timeIsShowing);
	};

	const handleTimeClick = (time) => {
		const currentValue = dateValue || new Date();

		// don't mutate the state
		let valueCopy = new Date(currentValue.valueOf());

		// update the time
		valueCopy = setMinutes(setHours(currentValue, time), 0);
		setDate(valueCopy);
	};

	const handleMonthChange = (direction) => {
		const nextMonth = addMonths(new Date(activeMonth.valueOf()), direction);
		changeActiveMonth(nextMonth);
	};

	const handleYearChange = (event: BaseSyntheticEvent) => {
		const val = event.target.value;
		const value = val.slice(0, 4);
		setYearValue(value);
		const isValidYear = /^\d{4}$/.test(value);
		if (isValidYear) {
			changeActiveMonth(setYear(new Date(activeMonth.valueOf()), value));
		}
	};

	const handleChange = (value: string) => {
		let dateCopy = new Date(value);

		if (hasTime) {
			dateCopy = setHours(dateCopy, selectedTimeValue);
		}
		setDate(dateCopy);
	};

	const giveYearInputFocus = () => {
		setYearHasFocus(true);
	};

	const removeYearInputFocus = () => {
		setYearHasFocus(false);
	};

	if (!isShowing) {
		return <div />;
	}

	const displayYear = yearHasFocus ? yearValue : format(activeMonth, "yyyy");

	const dayWidth = calendarWidth / 7;
	const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

	const [dayElements, dayCounts] = useMemo(() => {
		// the day where the calendar starts
		let startDate = subDays(new Date(activeMonth.valueOf()), getDay(activeMonth));

		const dayElements: React.JSX.Element[] = [];
		let daycount = getDaysInMonth(activeMonth) + getDay(activeMonth);
		daycount += 7 - (daycount % 7);

		for (let i = 0; i < daycount; i += 1) {
			const dateKey = format(startDate, "yyyy/MM/dd");
			const dateStyle = {
				width: dayWidth,
				left: (i % 7) * dayWidth,
				height: Config.dayHeight,
				lineHeight: `${Config.dayHeight}px`,
				top: Math.floor(i / 7) * Config.dayHeight,
			};
			const isActiveMonth = dateKey.split("/")[1] === format(activeMonth, "MM");
			const isActiveDay = Boolean(dateValue && format(dateValue, "yyyy/MM/dd") === dateKey);

			dayElements.push(
				<Day
					onClick={handleChange}
					date={dateKey}
					key={dateKey}
					isActiveDay={isActiveDay}
					isActiveMonth={isActiveMonth}
					style={dateStyle}
				>
					{format(startDate, "d")}
				</Day>
			);
			startDate = addDays(startDate, 1);
		}
		return [dayElements, daycount];
	}, [value, activeMonth, dayWidth]);

	return (
		<div data-testid="date-picker-calendar" className={`${css.calendar} calendar`} style={{ width: calendarWidth }}>
			<div className={`${css.currentMonth} current-month`}>
				<div>
					<div className={`${css.month} month`}>{format(activeMonth, "MMMM")}</div>
					<div className={`${css.yearInputContainer}`}>
						<input
							data-testid="year-input"
							onFocus={giveYearInputFocus}
							onBlur={removeYearInputFocus}
							className={`${css.yearInput}`}
							onChange={handleYearChange}
							value={displayYear}
						/>
					</div>
				</div>

				<div
					data-testid="next-month-button"
					onClick={() => handleMonthChange(1)}
					className={`${css.changeMonthButton} ${css.next} change-month-button next`}
				>
					<FontAwesomeIcon icon={faArrowAltCircleRight} />
				</div>
				<div
					data-testid="previous-month-button"
					onClick={() => handleMonthChange(-1)}
					className={`${css.changeMonthButton} ${css.previous} change-month-button previous`}
				>
					<FontAwesomeIcon icon={faArrowAltCircleLeft} />
				</div>
			</div>

			<div className={`${css.daysOfWeek} days-of-week`}>
				{daysOfWeek.map((day, i) => (
					<div key={day} style={{ left: i * dayWidth, width: dayWidth }} className={`${css.day} day`}>
						{day}
					</div>
				))}
			</div>

			<div className={`${css.calendarDays} calendar-days`} style={{ height: (dayCounts / 7) * Config.dayHeight }}>
				{dayElements}
			</div>

			{hasTime ? (
				<div className={`${css.timePickerContainer} time-picker-container`}>
					<div onClick={toggleTime} className={`${css.timePickerButton} time-picker-button`}>
						<FontAwesomeIcon icon={faClock} />
					</div>
					{timeIsShowing ? (
						<div className={`${css.timeOptions} time-options`}>
							{Config.timeOptions.map((item) => (
								<div
									onClick={() => handleTimeClick(item.value)}
									className={`${css.timeItem} time-item`}
									key={item.label}
								>
									{item.label}
								</div>
							))}
						</div>
					) : (
						""
					)}
				</div>
			) : (
				""
			)}
		</div>
	);
};
export default Calendar;
